"use client";

import { useEffect, useRef } from "react";
import { cn } from "@/lib/utils";

interface GradientOrbsProps {
  className?: string;
  count?: number;
  colors?: string[];
  blur?: number;
  speed?: "slow" | "medium" | "fast";
}

export function GradientOrbs({
  className,
  count = 3,
  colors = ["#B8A77D", "#C9B896", "#8A7D5C"],
  blur = 100,
  speed = "medium",
}: GradientOrbsProps) {
  const speedConfig = {
    slow: { duration: 20, delay: 0 },
    medium: { duration: 15, delay: 0 },
    fast: { duration: 10, delay: 0 },
  };

  const config = speedConfig[speed];

  return (
    <div className={cn("absolute inset-0 overflow-hidden pointer-events-none", className)}>
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className="absolute rounded-full animate-float"
          style={{
            background: `radial-gradient(circle, ${colors[i % colors.length]}40 0%, transparent 70%)`,
            width: `${300 + i * 100}px`,
            height: `${300 + i * 100}px`,
            left: `${20 + i * 30}%`,
            top: `${10 + i * 20}%`,
            filter: `blur(${blur}px)`,
            animationDelay: `${i * 2}s`,
            animationDuration: `${config.duration + i * 2}s`,
          }}
        />
      ))}
    </div>
  );
}

interface GridPatternProps {
  className?: string;
  opacity?: number;
  size?: number;
  color?: string;
}

export function GridPattern({
  className,
  opacity = 0.02,
  size = 60,
  color = "rgba(232, 226, 216",
}: GridPatternProps) {
  return (
    <div
      className={cn("absolute inset-0 pointer-events-none", className)}
      style={{
        backgroundImage: `
          linear-gradient(${color}, ${opacity}) 1px, transparent 1px),
          linear-gradient(90deg, ${color}, ${opacity}) 1px, transparent 1px)
        `,
        backgroundSize: `${size}px ${size}px`,
      }}
    />
  );
}

interface DotsPatternProps {
  className?: string;
  opacity?: number;
  size?: number;
  color?: string;
}

export function DotsPattern({
  className,
  opacity = 0.1,
  size = 20,
  color = "#B8A77D",
}: DotsPatternProps) {
  return (
    <div
      className={cn("absolute inset-0 pointer-events-none", className)}
      style={{
        backgroundImage: `radial-gradient(${color} ${opacity * 100}% , transparent ${opacity * 100}%)`,
        backgroundSize: `${size}px ${size}px`,
      }}
    />
  );
}

interface NoiseTextureProps {
  className?: string;
  opacity?: number;
}

export function NoiseTexture({
  className,
  opacity = 0.015,
}: NoiseTextureProps) {
  return (
    <div
      className={cn("fixed inset-0 pointer-events-none", className)}
      style={{
        opacity,
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
      }}
    />
  );
}

interface VignetteProps {
  className?: string;
  intensity?: "light" | "medium" | "heavy";
}

export function Vignette({
  className,
  intensity = "medium",
}: VignetteProps) {
  const intensityMap = {
    light: "rgba(5, 5, 5, 0.2)",
    medium: "rgba(5, 5, 5, 0.4)",
    heavy: "rgba(5, 5, 5, 0.6)",
  };

  return (
    <div
      className={cn("fixed inset-0 pointer-events-none", className)}
      style={{
        background: `radial-gradient(ellipse at center, transparent 0%, ${intensityMap[intensity]} 100%)`,
      }}
    />
  );
}

interface AuroraProps {
  className?: string;
  intensity?: "low" | "medium" | "high";
}

export function Aurora({
  className,
  intensity = "medium",
}: AuroraProps) {
  const opacityMap = {
    low: 0.03,
    medium: 0.05,
    high: 0.08,
  };

  const opacity = opacityMap[intensity];

  return (
    <div
      className={cn("absolute inset-0 overflow-hidden pointer-events-none", className)}
      style={{
        background: `
          radial-gradient(ellipse at 20% 20%, rgba(184, 167, 125, ${opacity}) 0%, transparent 50%),
          radial-gradient(ellipse at 80% 80%, rgba(184, 167, 125, ${opacity}) 0%, transparent 50%),
          radial-gradient(ellipse at 50% 50%, rgba(201, 184, 150, ${opacity * 0.6}) 0%, transparent 50%)
        `,
        animation: "aurora 20s ease-in-out infinite",
      }}
    />
  );
}

interface SpotlightProps {
  className?: string;
  mousePosition: { x: number; y: number };
  size?: number;
  opacity?: number;
  color?: string;
}

export function Spotlight({
  className,
  mousePosition,
  size = 400,
  opacity = 0.15,
  color = "#B8A77D",
}: SpotlightProps) {
  return (
    <div
      className={cn("fixed inset-0 pointer-events-none", className)}
      style={{
        background: `radial-gradient(${size}px circle at ${mousePosition.x}px ${mousePosition.y}px, ${color}${Math.round(opacity * 255).toString(16).padStart(2, "0")}, transparent 50%)`,
      }}
    />
  );
}

interface AnimatedLinesProps {
  className?: string;
  count?: number;
  color?: string;
  opacity?: number;
}

export function AnimatedLines({
  className,
  count = 5,
  color = "#B8A77D",
  opacity = 0.1,
}: AnimatedLinesProps) {
  return (
    <div className={cn("absolute inset-0 overflow-hidden pointer-events-none", className)}>
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className="absolute h-px animate-slide-right"
          style={{
            background: `linear-gradient(90deg, transparent, ${color}, transparent)`,
            opacity,
            top: `${10 + i * 20}%`,
            left: 0,
            right: 0,
            animationDelay: `${i * 0.5}s`,
            animationDuration: `${3 + i}s`,
          }}
        />
      ))}
    </div>
  );
}

interface FloatingShapesProps {
  className?: string;
  count?: number;
  shapes?: ("circle" | "square" | "triangle")[];
  colors?: string[];
}

export function FloatingShapes({
  className,
  count = 6,
  shapes = ["circle", "square", "triangle"],
  colors = ["#B8A77D", "#C9B896", "#8A7D5C"],
}: FloatingShapesProps) {
  return (
    <div className={cn("absolute inset-0 overflow-hidden pointer-events-none", className)}>
      {Array.from({ length: count }).map((_, i) => {
        const shape = shapes[i % shapes.length];
        const color = colors[i % colors.length];
        const size = 20 + Math.random() * 40;
        const left = Math.random() * 100;
        const top = Math.random() * 100;
        const delay = Math.random() * 5;
        const duration = 15 + Math.random() * 10;

        return (
          <div
            key={i}
            className="absolute animate-float"
            style={{
              left: `${left}%`,
              top: `${top}%`,
              width: size,
              height: size,
              backgroundColor: shape === "circle" ? color : "transparent",
              border: shape === "square" ? `2px solid ${color}` : "none",
              borderRadius: shape === "circle" ? "50%" : shape === "square" ? "4px" : "0",
              opacity: 0.2,
              animationDelay: `${delay}s`,
              animationDuration: `${duration}s`,
              ...(shape === "triangle" && {
                width: 0,
                height: 0,
                borderLeft: `${size / 2}px solid transparent`,
                borderRight: `${size / 2}px solid transparent`,
                borderBottom: `${size}px solid ${color}`,
              }),
            }}
          />
        );
      })}
    </div>
  );
}

export default {
  GradientOrbs,
  GridPattern,
  DotsPattern,
  NoiseTexture,
  Vignette,
  Aurora,
  Spotlight,
  AnimatedLines,
  FloatingShapes,
};
